import React from 'react';
import { useVisualizationStore } from '../store/visualizationStore';
import { useZoomTransition } from './ZoomTransition';

export const NavigationPanel: React.FC = () => {
  const {
    viewport,
    nodes,
    currentContext,
    contextPath,
    setContext,
    setViewport
  } = useVisualizationStore();

  const { zoomBy } = useZoomTransition();

  const getNodeIcon = (type: string) => {
    const icons = {
      site: '🌍',
      pod: '🏢',
      network: '🔗',
      rack: '📦',
      device: '💾',
      component: '🔧'
    };
    return icons[type as keyof typeof icons] || '📍';
  };

  const handleBreadcrumbClick = (contextId: string | null) => {
    // setContext will reset the viewport to identity transform
    // Don't call zoomToNode as it would use stale node positions
    setContext(contextId);
  };

  const handleZoomIn = () => {
    zoomBy(1.5, window.innerWidth / 2, window.innerHeight / 2).catch(() => {
      // Ignore transition interruptions
    });
  };

  const handleZoomOut = () => {
    zoomBy(0.67, window.innerWidth / 2, window.innerHeight / 2).catch(() => {
      // Ignore transition interruptions
    });
  };

  const handleFitToView = () => {
    if (nodes.length === 0) return;
    
    // Filter out nodes without valid positions
    const validNodes = nodes.filter(n => 
      typeof n.x === 'number' && typeof n.y === 'number' && 
      !isNaN(n.x) && !isNaN(n.y)
    );
    if (validNodes.length === 0) return;
    
    // Calculate the content bounds
    const xs = validNodes.map(n => n.x);
    const ys = validNodes.map(n => n.y);
    const padding = 100;
    const minX = Math.min(...xs) - padding;
    const maxX = Math.max(...xs) + padding;
    const minY = Math.min(...ys) - padding;
    const maxY = Math.max(...ys) + padding;
    
    // Calculate what we need to fit
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;
    
    // Calculate required zoom to fit content in viewport
    const targetZoom = Math.min(
      (window.innerWidth - 200) / contentWidth,
      (window.innerHeight - 200) / contentHeight,
      10
    );
    
    // Step 1: Set zoom to target level (this should center around current position)
    const zoomFactor = targetZoom / viewport.zoom;
    zoomBy(zoomFactor, window.innerWidth / 2, window.innerHeight / 2).catch(() => {
      // Ignore transition interruptions
    });
    
    // Step 2: Calculate where content center will be after zoom, then pan to center it
    setTimeout(() => {
      // After zoom is applied, calculate offset needed to center content
      const newViewport = useVisualizationStore.getState().viewport;
      
      // Where is content center in screen coordinates after zoom?
      const contentScreenX = contentCenterX * newViewport.zoom + newViewport.x;
      const contentScreenY = contentCenterY * newViewport.zoom + newViewport.y;
      
      // How much do we need to pan to center it?
      const panX = window.innerWidth / 2 - contentScreenX;
      const panY = window.innerHeight / 2 - contentScreenY;
      
      // Apply the pan
      setViewport({
        x: newViewport.x + panX,
        y: newViewport.y + panY,
        zoom: newViewport.zoom
      });
    }, 100); // Small delay to let zoom complete
  };


  return (
    <div className="navigation-panel bg-white shadow-lg rounded-lg p-4 absolute top-4 left-4 z-10 min-w-80">
      {/* Context Navigation */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold mb-2 text-gray-700">Navigation</h3>
        <div className="flex flex-col space-y-1">
          <button
            onClick={() => handleBreadcrumbClick(null)}
            className={`px-3 py-2 text-sm rounded transition-colors ${
              !currentContext
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🌍 Global View - All Sites
          </button>
          {contextPath.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleBreadcrumbClick(item.id)}
              className={`px-3 py-2 text-sm rounded transition-colors ${
                currentContext === item.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex justify-between items-center">
                <span>{getNodeIcon(item.type)} {item.name}</span>
                <span className="text-xs opacity-75">
                  {item.type}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold mb-2 text-gray-700">Zoom Controls</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleZoomIn}
            className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
          >
            Zoom In
          </button>
          <button
            onClick={handleZoomOut}
            className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
          >
            Zoom Out
          </button>
          <button
            onClick={handleFitToView}
            className="px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm"
          >
            Fit All
          </button>
        </div>
      </div>

      {/* Current Info */}
      <div className="mb-4 p-2 bg-gray-50 rounded">
        <div className="text-xs text-gray-600">
          <div>Zoom: {viewport.zoom.toFixed(2)}x</div>
          <div>Context: {currentContext ? contextPath[contextPath.length - 1]?.name || 'Unknown' : 'Global'}</div>
          <div>Position: ({viewport.x.toFixed(0)}, {viewport.y.toFixed(0)})</div>
        </div>
      </div>


      {/* Instructions */}
      <div className="text-xs text-gray-500">
        <div className="mb-1">• Click nodes to drill down into hierarchy</div>
        <div className="mb-1">• Use navigation buttons to go back</div>
        <div>• Zoom controls adjust scale only</div>
      </div>
    </div>
  );
};
